import { Progress, Group, Stack, List, Text } from "@mantine/core";
import { evaluatePass } from "@project/common";
import { useCallback } from "react";
import { FormattedMessage, useIntl } from "react-intl";

function PasswordFeedback({ warning, suggestions }: { warning: string | null; suggestions: string[] }) {
  const messages = [warning, ...suggestions].filter(Boolean);

  if (!messages.length) {
    return null;
  }

  return (
    <Stack gap="xs">
      <Text size="sm">
        <FormattedMessage id="label.passwordFeedback" defaultMessage="Tips for strong password:" />
      </Text>
      <List
        size="sm"
        center
        spacing="xs"
      >
        {messages.map(message => <List.Item key={message}>{message}</List.Item>)}
      </List>
    </Stack>

  );
}

export interface PasswordStrengthProps {
  newPassword?: string;
  password?: string;
  passwordConfirmation?: string;

}

const progressStyles = {
  section: {
    transitionDuration: "0ms",
  },
};

export function PasswordStrength({ password, newPassword, passwordConfirmation }: PasswordStrengthProps) {
  const intl = useIntl();

  const feedback = useCallback((score: number, contains: string[]) => {
    const suggestions = [];

    if (!contains.includes("symbol")) {
      suggestions.push(intl.formatMessage({
        defaultMessage: "Add symbols like .@, ?, !, etc.",
        id: "suggestion.addSymbolsSuggestion",
      }));
    }
    if (!contains.includes("number")) {
      suggestions.push(intl.formatMessage({
        defaultMessage: "Add numbers",
        id: "suggestion.addNumbersSuggestion",
      }));
    }
    if (!contains.includes("uppercase")) {
      suggestions.push(intl.formatMessage({
        defaultMessage: "Add uppercase letters",
        id: "suggestion.addUppercaseSuggestion",
      }));
    }
    if (!contains.includes("lowercase")) {
      suggestions.push(intl.formatMessage({
        defaultMessage: "Make password longer",
        id: "suggestion.addLowercaseSuggestion",
      }));
    }

    if (score === 0) {
      return {
        warning: intl.formatMessage({
          defaultMessage: "Password is too weak",
          id: "warning.tooWeak",
        }),
        suggestions,
      };
    }
    else if (score === 1) {
      return {
        warning: intl.formatMessage({
          defaultMessage: "Password is weak",
          id: "warning.weak",
        }),
        suggestions,
      };
    }
    return {
      suggestions: [] as string[],
      warning: null,
    };
  }, [intl]);

  const {
    score,
    contains,
  } = evaluatePass(password ?? newPassword ?? passwordConfirmation ?? "");

  const {
    warning,
    suggestions,
  } = feedback(score, contains);

  const bars = Array(4)
    .fill(0)
    .map((_, index) => {
      let color = "red";
      if (score >= 3) {
        color = "teal";
      }
      else if (score >= 2) {
        color = "yellow";
      }

      return (
        <Progress
          styles={progressStyles}
          value={score >= index ? 100 : 0}
          color={color}
          key={index}
          size={4}
        />
      );
    });

  return (
    <Stack gap={5}>
      <Group gap={5} grow mt="xs">
        {bars}
      </Group>
      <PasswordFeedback warning={warning} suggestions={suggestions} />
    </Stack>

  );
}
